import dayjs from 'dayjs';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  DEFAULT_ADULT,
  DEFAULT_CHILDREN_AGES,
  DEFAULT_ROOMS,
} from '@travel/traveler-core/constants';
import { useDialogHandler } from '@travel/traveler-core/hooks';
import useDeviceType, { DESKTOP_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { FlatButton, Popup } from '@travel/ui';
import { cx } from '@travel/utils';

import DateInput, { Props as DateInputProps } from 'components/DateInput';
import {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';

import { getIsFetching } from 'store/bookingStep/selectors';
import { getMember } from 'store/member/selectors';

import { Translate } from 'core/translate';
import useFetchVacancyCalendarData from 'hooks/useFetchVacancyCalendarData';
import { RoomFormQuery } from 'ProviderList-Types';

import NoAccountNeededPopup from '../NoAccountNeededPopup';

import styles from './reserveButton.module.scss';

export type Props = {
  wrapperClassName?: string;
  className?: string;
  /** Callback to call when click reserve button (reserve action)  */
  onClick: () => void;
  /** Current search params */
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  /** Callback to be called wheh date is selected */
  onSelectDate?: (startDate?: string, endDate?: string) => void;
  /** should show no account needed message */
  shouldShowNoAccountNeeded?: boolean;
  /** should show Vacancy Calendar component */
  shouldShowVacancyCalendar?: boolean;
  /** itemRatePlanId from card */
  itemRatePlanId?: string;
};

function ReserveButton(props: Props) {
  const {
    searchParam,
    onSelectDate,
    shouldShowNoAccountNeeded = true,
    shouldShowVacancyCalendar = false,
    itemRatePlanId,
  } = props;
  const isDatedSearch = searchParam.startDate || searchParam.endDate;
  const isFetching = useSelector(getIsFetching);
  const { isMember: isLogin } = useSelector(getMember);
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const {
    isOpen: isOpenCheckAvailability,
    onOpen: onOpenCheckAvailability,
    onClose: onCloseCheckAvailability,
  } = useDialogHandler(false);
  const isPC = useDeviceType() === DESKTOP_SCREEN;

  const { onClickFetchVacancyCalendar: onClickFetch } = useFetchVacancyCalendarData();

  const startMonth = dayjs(searchParam.startDate).format('YYYY-MM');
  const nextMonth = dayjs(searchParam.startDate)
    .add(1, 'month')
    .format('YYYY-MM');

  const onClickFetchVacancyCalendar = () => {
    if (shouldShowVacancyCalendar) {
      onClickFetch(
        Number(searchParam.adults) || DEFAULT_ADULT,
        searchParam.childrenAges
          ? searchParam?.childrenAges?.split(',').map(Number)
          : DEFAULT_CHILDREN_AGES,
        Number(searchParam.noOfUnits) || DEFAULT_ROOMS,
        searchParam.startDate,
        searchParam.endDate,
        [startMonth, nextMonth],
        itemRatePlanId,
        searchParam.salesPromotionId,
      );
    }
  };

  const customDatePickerProps: DateInputProps['customDatePickerProps'] = {
    dialogTitleString: <Translate id="Provider_Info.Vacancy_Calendar.Title" />,
    calendarBodyClass: styles.calendarBodyWrapper,
  };

  return (
    <>
      {isDatedSearch ? (
        <div className={styles.reserveButtonWrapper}>
          <FlatButton
            className={cx(props.className, styles.reserveButton)}
            ariaLabel="Reserve"
            onClick={props.onClick}
            isSmallerButtonByDefault={true}
            isLoading={isFetching}
            data-testid="reservation-button"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
          >
            <Translate id="Provider_Info.Room_List.Plan_Info.Reserve" />
          </FlatButton>

          {shouldShowNoAccountNeeded &&
            !isLogin &&
            (isPC ? <NoAccountNeededPopup isOpen={isOpen} /> : null)}

          {shouldShowVacancyCalendar && (
            <DateInput
              className={cx(props.className, styles.dateInputButton, styles.datedDateInputButton)}
              onPage="vacancyCalendar"
              onDateChanged={onSelectDate}
              onClickStartDateCallBack={onClickFetchVacancyCalendar}
              searchParam={searchParam}
              itemRatePlanId={itemRatePlanId}
              customDatePickerProps={customDatePickerProps}
              onDateButtonMouseEnter={onOpenCheckAvailability}
              onDateButtonMouseLeave={onCloseCheckAvailability}
            />
          )}

          {isPC && isOpenCheckAvailability ? (
            <Popup
              isOpen={isOpenCheckAvailability}
              onClose={() => {}}
              className={styles.messageWrapper}
            >
              <Translate id="Provider_Info.Plan_List.Room_Info.Check_Availability" />
            </Popup>
          ) : null}
        </div>
      ) : (
        <DateInput
          className={cx(props.className, styles.dateInputButton)}
          onPage={shouldShowVacancyCalendar ? 'vacancyCalendarUndated' : 'rateplan'}
          onDateChanged={onSelectDate}
          onClickStartDateCallBack={onClickFetchVacancyCalendar}
          searchParam={searchParam}
          itemRatePlanId={itemRatePlanId}
          customDatePickerProps={shouldShowVacancyCalendar ? customDatePickerProps : undefined}
        />
      )}
    </>
  );
}

export default ReserveButton;
